import { apiMessages } from 'ui/messages';

import includeRecaptchaJs from './includeRecaptchaJs';
import recaptchaModes from './recaptchaModes';

let checkboxKey;
let scoreKey;
let checkboxWidgetContainer;
let checkboxWidgetClientId;
let checkboxWidgetLabelContainer;

const validateScoreKey = () => {

	if (!scoreKey) {

		throw new Error('Missing site Key for reCAPTCHA Enterprise SCORE.');

	}

};

const validateCheckboxKey = () => {

	if (!checkboxKey) {

		throw new Error('Missing site Key for reCAPTCHA Enterprise CHECKBOX.');

	}

};

const toggleWidgetLabelVisibility = (show) => {

	if (checkboxWidgetLabelContainer) {

		const method = show ? 'remove' : 'add';
		checkboxWidgetLabelContainer.classList[method]('hidden');

	}

};

const toggleWidgetVisibility = (show) => {

	if (checkboxWidgetContainer) {

		const method = show ? 'remove' : 'add';
		checkboxWidgetContainer.classList[method]('hidden');
		toggleWidgetLabelVisibility(show);

	}

};

const getCheckboxToken = () => (!window.grecaptcha || !window.grecaptcha.enterprise || typeof checkboxWidgetClientId === 'undefined'
	? ''
	: window.grecaptcha.enterprise.getResponse(checkboxWidgetClientId));

const getScoreToken = (action) => {

	// offline connection causes recaptcha v3 to throw a JS error
	// so let's detect offline connection and throw our own error message right away.
	if (typeof navigator.onLine === 'boolean' && !navigator.onLine) {

		return Promise.reject(new Error(apiMessages.error));

	}

	const executeRecaptchaScore = () => window.grecaptcha.enterprise.execute(scoreKey, { action });

	try {

		// Always re-generate a new captcha token to prevent sending an expired token
		// If we generated token only once on page load, it may expire if user loaded the page
		// but clicked Apply button at a later time
		return executeRecaptchaScore();

	} catch (e) {

		return includeRecaptchaJs(scoreKey)
			.then(() => executeRecaptchaScore())
			.catch(() => Promise.resolve('')); // Pass a blank token if Recaptcha is not available

	}

};

const isWidgetVisible = () => checkboxWidgetContainer && window.getComputedStyle(checkboxWidgetContainer).display !== 'none';

const getActiveIntegrationType = ({ scoreType, checkboxType }) => (typeof checkboxWidgetClientId !== 'undefined' && isWidgetVisible() ? checkboxType : scoreType);

/**
 * Renders reCAPTCHA Enterprise CHECKBOX widget
 * @param {String} widgetContainer ID or the DOM element container for the reCAPTCHA CHECKBOX widget (Required)
 * @param {String} action Action name associated with the form/page that contains this Recaptcha
 * @param {Function} expiredCallback Callback function that runs when reCAPTCHA response expires.
 * @param {Function} successCallback Callback function that runs when user submits a successful response.
 * Token is passed as parameter to the callback.
 * @param {Function} errorCallback Callback function that runs when reCAPTCHA encounters an error, usually a network
 * connectivity error. If specified, the caller is responsible for informing the user they should re-try.
 * Otherwise, by default, Google shows an alert dialog, "Cannot contact reCAPTCHA. Check your connection and try again".
 * @param {String} theme Color theme of the widget. Values are 'dark' and 'light' (default).
 * @param {String} size Size of the widget. Values are 'compact' and 'normal' (default).
 * @param {Number} tabindex The tabindex of the widget and challenge.
 * @param {String} widgetLabelContainer The DOM element that refers to the widget label text element
 * @returns A Promise oject that resolves when the widget has rendered.
 */
const renderWidget = ({
	widgetContainer,
	action,
	expiredCallback,
	successCallback,
	errorCallback,
	theme = 'light',
	size = 'normal',
	tabindex = 0,
	widgetLabelContainer
}) => {

	validateCheckboxKey();

	const render = () => {

		checkboxWidgetClientId = window.grecaptcha.enterprise.render(widgetContainer, {
			sitekey: checkboxKey,
			action,
			callback: (token) => {

				toggleWidgetLabelVisibility(false);

				if (typeof successCallback === 'function') {

					successCallback(token);

				}

			},
			'error-callback': errorCallback,
			'expired-callback': expiredCallback,
			theme,
			size,
			tabindex
		});

		checkboxWidgetContainer = document.getElementById(widgetContainer) || widgetContainer;

		checkboxWidgetLabelContainer = widgetLabelContainer;

		toggleWidgetVisibility(true);

	};

	if (window.grecaptcha && window.grecaptcha.enterprise && window.grecaptcha.enterprise.render) {

		render();
		return Promise.resolve();

	}

	return includeRecaptchaJs().then(render);

};

const resetWidget = () => {

	if (window.grecaptcha && window.grecaptcha.enterprise && typeof checkboxWidgetClientId !== 'undefined') {

		window.grecaptcha.enterprise.reset(checkboxWidgetClientId);

	}

};

const isWidgetRendered = () => typeof checkboxWidgetClientId !== 'undefined';

const recaptcha = ({ siteKeyCheckbox, siteKeyScore, mode = recaptchaModes.MODE_SCORE_THEN_CHECKBOX }) => {

	if ([ recaptchaModes.MODE_SCORE_THEN_CHECKBOX, recaptchaModes.MODE_SCORE_ONLY ].includes(mode)) {

		scoreKey = siteKeyScore;
		validateScoreKey();

	}

	if ([ recaptchaModes.MODE_SCORE_THEN_CHECKBOX, recaptchaModes.MODE_CHECKBOX_ONLY ].includes(mode)) {

		checkboxKey = siteKeyCheckbox;
		validateCheckboxKey();

	}

	return {
		getActiveIntegrationType,
		checkbox: {
			getToken: getCheckboxToken,
			renderWidget,
			resetWidget,
			toggleWidgetVisibility,
			isWidgetRendered,
			isWidgetVisible
		},
		score: {
			getToken: getScoreToken
		}
	};

};

export default recaptcha;
